import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, MessageSquare, FileText, Bot, Settings, Plus, Send, Trash2, X, Phone } from "lucide-react";
import { toast } from "sonner";
import useRealtime from "@/lib/realtime";
import { useSearchParams } from "react-router-dom";
import { formatDateTime } from "@/lib/format";

const TAB_KEYS = [
    { key: "messages",     i18n: "messages",     icon: MessageSquare },
    { key: "templates",    i18n: "templates",    icon: FileText },
    { key: "auto-replies", i18n: "auto_replies", icon: Bot },
    { key: "channels",     i18n: "channels",     icon: Settings },
];

export default function Inbox() {
    const { t } = useTranslation();
    const [tab, setTab] = useState("messages");
    return (
        <>
            <Topbar title={t("inbox.title")} subtitle={t("inbox.subtitle")} />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mb-4 flex gap-1 border-b border-border" data-testid="inbox-tabs">
                    {TAB_KEYS.map((tt) => {
                        const Icon = tt.icon;
                        return (
                            <button key={tt.key} onClick={() => setTab(tt.key)}
                                    data-testid={`inbox-tab-${tt.key}`}
                                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                        tab === tt.key ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}>
                                <Icon size={13} />
                                {t(`inbox.tabs.${tt.i18n}`)}
                            </button>
                        );
                    })}
                </div>
                {tab === "messages"  && <MessagesPane />}
                {tab === "templates" && <TemplatesPane />}
                {tab === "auto-replies" && <AutoRepliesPane />}
                {tab === "channels"  && <ChannelsPane />}
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Messages — list + thread view
// ─────────────────────────────────────────────────────────────
function MessagesPane() {
    const { t } = useTranslation();
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null);
    const [params, setParams] = useSearchParams();
    const load = async () => {
        const { data } = await api.get("/messages/threads");
        setThreads(data);
        const tt = params.get("thread");
        if (tt && !active) setActive(tt);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useRealtime((msg) => {
        if (msg?.event === "message.inbound" || msg?.event === "message.sent") load();
    });
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]" data-testid="inbox-messages">
            <div className="akm-card overflow-hidden">
                <div className="border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("inbox.threads", { count: threads.length })}
                </div>
                <ul className="max-h-[640px] overflow-y-auto">
                    {threads.map((tt) => (
                        <li key={tt.id}>
                            <button onClick={() => { setActive(tt.id); setParams({ thread: tt.id }); }}
                                    data-testid={`thread-${tt.id}`}
                                    className={`w-full border-b border-border px-3 py-2.5 text-left text-[12.5px] transition hover:bg-muted/40 ${
                                        active === tt.id ? "bg-[#ff7b02]/5" : ""
                                    }`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="truncate font-semibold">{tt.guest_name}</div>
                                    {tt.unread_for_staff > 0 && (
                                        <span className="rounded-full bg-[#ff7b02] px-1.5 text-[9px] font-semibold text-white">
                                            {tt.unread_for_staff}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                    {tt.last_message_preview || "—"}
                                </div>
                            </button>
                        </li>
                    ))}
                    {threads.length === 0 && (
                        <li className="py-10 text-center text-[12px] text-muted-foreground">{t("inbox.no_conversations")}</li>
                    )}
                </ul>
            </div>
            {active ? <ThreadView threadId={active} key={active} onChanged={load} /> : (
                <div className="akm-card flex items-center justify-center p-10 text-[12px] text-muted-foreground">
                    {t("inbox.select_conversation")}
                </div>
            )}
        </div>
    );
}

function ThreadView({ threadId, onChanged }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [text, setText] = useState("");
    const [internal, setInternal] = useState(false);
    const [channel, setChannel] = useState("in_app");
    const load = async () => {
        const { data } = await api.get(`/messages/threads/${threadId}`);
        setData(data);
        try { await api.post(`/messages/threads/${threadId}/mark-read`); } catch { /* ignore */ }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);
    useRealtime((msg) => {
        if ((msg?.event === "message.inbound" || msg?.event === "message.sent") && msg.payload?.thread_id === threadId) {
            load();
        }
    });
    const send = async () => {
        if (!text.trim()) return;
        try {
            await api.post(`/messages/threads/${threadId}/send`, { body: text, channel, internal });
            setText("");
            await load();
            await onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const simulate = async () => {
        try {
            await api.post(`/messages/threads/${threadId}/inbound`, { body: "Hi, can I get a late check-out?", channel });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    if (!data) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
    const th = data.thread;
    return (
        <div className="akm-card flex flex-col" style={{ height: "640px" }} data-testid="thread-view">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <div>
                    <div className="text-[13px] font-semibold">{th.guest_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                        {th.reservation_id ? t("inbox.reservation_prefix", { id: th.reservation_id.slice(0, 8) }) : ""}{th.channel}
                    </div>
                </div>
                <button onClick={simulate} className="akm-btn-ghost text-[11px]" data-testid="thread-simulate">
                    {t("inbox.simulate_inbound")}
                </button>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="thread-messages">
                {data.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-[12.5px] ${
                            m.internal ? "border border-amber-200 bg-amber-50 text-amber-900" :
                            m.direction === "outbound" ? "bg-[#ff7b02] text-white" : "bg-muted text-foreground"
                        }`}>
                            {m.internal && <div className="mb-0.5 text-[9px] font-bold uppercase tracking-wider">{t("inbox.internal_note")}</div>}
                            <div className="whitespace-pre-wrap">{m.body}</div>
                            <div className={`mt-1 text-[10px] ${m.direction === "outbound" && !m.internal ? "text-white/70" : "text-muted-foreground"}`}>
                                {m.author_name || (m.direction === "inbound" ? t("inbox.guest_default") : t("inbox.staff_default"))} · {formatDateTime(m.created_at)}
                                {m.delivery_status && m.direction === "outbound" && ` · ${m.delivery_status}`}
                            </div>
                        </div>
                    </div>
                ))}
                {data.messages.length === 0 && (
                    <div className="py-12 text-center text-[12px] text-muted-foreground">{t("inbox.no_messages")}</div>
                )}
            </div>
            <div className="border-t border-border p-3 space-y-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)}
                          placeholder={t("inbox.type_message")}
                          data-testid="thread-input"
                          rows={2}
                          className="akm-input w-full resize-none text-[13px]" />
                <div className="flex flex-wrap items-center gap-2">
                    <select value={channel} onChange={(e) => setChannel(e.target.value)}
                            data-testid="thread-channel" className="akm-input h-7 text-[11px]">
                        <option value="in_app">{t("inbox.channel.in_app")}</option>
                        <option value="email">{t("inbox.channel.email")}</option>
                        <option value="sms">{t("inbox.channel.sms")}</option>
                        <option value="whatsapp">{t("inbox.channel.whatsapp")}</option>
                    </select>
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="thread-internal" />
                        {t("inbox.internal_note_help")}
                    </label>
                    <button onClick={send} disabled={!text.trim()} className="akm-btn-primary ml-auto text-[11px]" data-testid="thread-send">
                        <Send size={12} /> {t("inbox.send")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────
function TemplatesPane() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRows((await api.get("/messages/templates")).data);
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm(t("inbox.templates.delete_confirm"))) return;
        await api.delete(`/messages/templates/${id}`);
        await load();
    };
    return (
        <div data-testid="inbox-templates">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">{t("inbox.templates.count", { count: rows.length })}</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary text-[12px]" data-testid="add-template">
                    <Plus size={12} /> {t("inbox.templates.new")}
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">{t("inbox.templates.name")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.templates.channel")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.templates.category")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.templates.subject")}</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-muted-foreground capitalize">{r.channel}</td>
                                <td className="px-3 py-2 text-muted-foreground capitalize">{r.category}</td>
                                <td className="px-3 py-2 text-muted-foreground truncate max-w-[300px]">{r.subject || "-"}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-muted-foreground">{t("inbox.templates.empty")}</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && <TemplateModal onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}

function TemplateModal({ onClose, onSaved }) {
    const { t } = useTranslation();
    const [f, setF] = useState({ name: "", subject: "", body: "", channel: "email", category: "general", language: "en" });
    const submit = async () => {
        try {
            await api.post("/messages/templates", f);
            toast.success(t("inbox.templates.created"));
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <Modal title={t("inbox.templates.form_title")} onClose={onClose}>
            <div className="space-y-3">
                <Input label={t("inbox.templates.name")} value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="tpl-name" />
                <div className="grid grid-cols-2 gap-3">
                    <Select label={t("inbox.templates.channel")} value={f.channel} onChange={(v) => setF({ ...f, channel: v })} testid="tpl-channel"
                            options={[["in_app",t("inbox.channel.in_app")],["email",t("inbox.channel.email")],["sms",t("inbox.channel.sms")],["whatsapp",t("inbox.channel.whatsapp")]]} />
                    <Select label={t("inbox.templates.category")} value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="tpl-cat"
                            options={[["general",t("inbox.templates.categories.general")],["booking",t("inbox.templates.categories.booking")],["payment",t("inbox.templates.categories.payment")],["arrival",t("inbox.templates.categories.arrival")],["departure",t("inbox.templates.categories.departure")],["marketing",t("inbox.templates.categories.marketing")]]} />
                </div>
                <Input label={t("inbox.templates.subject_only")} value={f.subject} onChange={(v) => setF({ ...f, subject: v })} testid="tpl-subj" />
                <label className="block">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("inbox.templates.body", { vars: "{{guest_name}}, {{check_in}}" })}</div>
                    <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={5}
                              data-testid="tpl-body"
                              className="akm-input w-full text-[13px] resize-none" />
                </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost text-[12px]">{t("common.cancel")}</button>
                <button onClick={submit} className="akm-btn-primary text-[12px]" data-testid="tpl-submit">{t("inbox.templates.create")}</button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Auto-replies
// ─────────────────────────────────────────────────────────────
function AutoRepliesPane() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRows((await api.get("/messages/auto-replies")).data);
    useEffect(() => { load(); }, []);
    const remove = async (id) => {
        if (!window.confirm(t("inbox.auto.delete_confirm"))) return;
        await api.delete(`/messages/auto-replies/${id}`);
        await load();
    };
    const toggle = async (r) => { await api.patch(`/messages/auto-replies/${r.id}`, { active: !r.active }); await load(); };
    return (
        <div data-testid="inbox-autos">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">{t("inbox.auto.count", { count: rows.length })}</div>
                <button onClick={() => setOpen(true)} className="akm-btn-primary text-[12px]" data-testid="add-auto">
                    <Plus size={12} /> {t("inbox.auto.new")}
                </button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">{t("inbox.auto.name")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.auto.keywords")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.auto.channel")}</th>
                            <th className="px-3 py-2 text-left">{t("inbox.auto.preview")}</th>
                            <th className="px-3 py-2">{t("inbox.auto.active_col")}</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{r.name}</td>
                                <td className="px-3 py-2 text-muted-foreground text-[11px]">{(r.keywords || []).join(", ")}</td>
                                <td className="px-3 py-2 text-muted-foreground capitalize">{r.channel}</td>
                                <td className="px-3 py-2 text-muted-foreground truncate max-w-[300px]">{r.body}</td>
                                <td className="px-3 py-2 text-center">
                                    <button onClick={() => toggle(r)} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                        r.active ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"
                                    }`}>
                                        {r.active ? t("inbox.auto.active") : t("inbox.auto.off")}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-muted-foreground">{t("inbox.auto.empty")}</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && <AutoModal onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
        </div>
    );
}

function AutoModal({ onClose, onSaved }) {
    const { t } = useTranslation();
    const [f, setF] = useState({ name: "", keywords: "", body: "", channel: "in_app", active: true });
    const submit = async () => {
        try {
            await api.post("/messages/auto-replies", {
                ...f, keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean),
            });
            toast.success(t("inbox.auto.created"));
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <Modal title={t("inbox.auto.form_title")} onClose={onClose}>
            <div className="space-y-3">
                <Input label={t("inbox.auto.name")} value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="auto-name" />
                <Input label={t("inbox.auto.trigger_keywords")} value={f.keywords} onChange={(v) => setF({ ...f, keywords: v })} testid="auto-kw" />
                <Select label={t("inbox.auto.channel")} value={f.channel} onChange={(v) => setF({ ...f, channel: v })} testid="auto-channel"
                        options={[["in_app",t("inbox.channel.in_app")],["email",t("inbox.channel.email")],["sms",t("inbox.channel.sms")],["whatsapp",t("inbox.channel.whatsapp")]]} />
                <label className="block">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("inbox.auto.reply_body")}</div>
                    <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={4}
                              data-testid="auto-body" className="akm-input w-full text-[13px] resize-none" />
                </label>
                <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
                    {t("inbox.auto.active")}
                </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="akm-btn-ghost text-[12px]">{t("common.cancel")}</button>
                <button onClick={submit} className="akm-btn-primary text-[12px]" data-testid="auto-submit">{t("inbox.auto.create")}</button>
            </div>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// Channels settings — provider config (placeholders for SendGrid/Twilio)
// ─────────────────────────────────────────────────────────────
function ChannelsPane() {
    const { t } = useTranslation();
    const [s, setS] = useState(null);
    const [test, setTest] = useState({ channel: "email", to_email: "", to_phone: "" });
    const load = async () => setS((await api.get("/notifications/settings")).data);
    useEffect(() => { load(); }, []);
    const save = async () => {
        try {
            await api.put("/notifications/settings", s);
            toast.success(t("inbox.settings.saved"));
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const sendTest = async () => {
        try {
            const { data } = await api.post("/notifications/test-send", {
                ...test, title: t("inbox.settings.test_title"), body: t("inbox.settings.test_body", { channel: test.channel }),
            });
            toast.success(t("inbox.settings.delivered", { list: data.delivered.join(", ") || t("common.none") }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    if (!s) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
    const upd = (ch, field, v) => setS({ ...s, providers: { ...s.providers, [ch]: { ...s.providers[ch], [field]: v } } });
    return (
        <div className="space-y-4" data-testid="inbox-settings">
            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("inbox.settings.email_provider")}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Select label={t("inbox.settings.provider")} value={s.providers.email.provider} onChange={(v) => upd("email","provider",v)}
                            options={[["mock","Mock (no real sending)"],["sendgrid","SendGrid"],["resend","Resend"],["mailgun","Mailgun"]]} />
                    <Input label={t("inbox.settings.from_email")} value={s.providers.email.from_email} onChange={(v) => upd("email","from_email",v)} />
                    <Input label={t("inbox.settings.api_key")} value={s.providers.email.api_key} onChange={(v) => upd("email","api_key",v)} type="password" />
                </div>
                <Hint configured={!!s.providers.email.api_key} />
            </div>
            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("inbox.settings.sms_provider")}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Select label={t("inbox.settings.provider")} value={s.providers.sms.provider} onChange={(v) => upd("sms","provider",v)}
                            options={[["twilio","Twilio"],["vonage","Vonage"]]} />
                    <Input label={t("inbox.settings.from_number")} value={s.providers.sms.from_number} onChange={(v) => upd("sms","from_number",v)} />
                    <Input label={t("inbox.settings.account_sid")} value={s.providers.sms.account_sid} onChange={(v) => upd("sms","account_sid",v)} />
                    <Input label={t("inbox.settings.auth_token")} value={s.providers.sms.auth_token} onChange={(v) => upd("sms","auth_token",v)} type="password" />
                </div>
                <Hint configured={!!s.providers.sms.account_sid && !!s.providers.sms.auth_token} />
            </div>
            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("inbox.settings.whatsapp_provider")}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Select label={t("inbox.settings.provider")} value={s.providers.whatsapp.provider} onChange={(v) => upd("whatsapp","provider",v)}
                            options={[["twilio_whatsapp","Twilio WhatsApp"],["meta_cloud","Meta Cloud API"]]} />
                    <Input label={t("inbox.settings.from_number")} value={s.providers.whatsapp.from_number} onChange={(v) => upd("whatsapp","from_number",v)} />
                    <Input label={t("inbox.settings.account_sid_or_app")} value={s.providers.whatsapp.account_sid} onChange={(v) => upd("whatsapp","account_sid",v)} />
                    <Input label={t("inbox.settings.auth_token_or_access")} value={s.providers.whatsapp.auth_token} onChange={(v) => upd("whatsapp","auth_token",v)} type="password" />
                </div>
                <Hint configured={!!s.providers.whatsapp.account_sid} />
            </div>

            <div className="akm-card p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("inbox.settings.reminders")}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input label={t("inbox.settings.reminder_in")} type="number" value={s.reminder_check_in_hours_before}
                           onChange={(v) => setS({ ...s, reminder_check_in_hours_before: parseInt(v, 10) || 0 })} />
                    <Input label={t("inbox.settings.reminder_out")} type="number" value={s.reminder_check_out_hours_before}
                           onChange={(v) => setS({ ...s, reminder_check_out_hours_before: parseInt(v, 10) || 0 })} />
                    <Input label={t("inbox.settings.review_request")} type="number" value={s.review_request_after_hours}
                           onChange={(v) => setS({ ...s, review_request_after_hours: parseInt(v, 10) || 0 })} />
                </div>
            </div>

            <button onClick={save} className="akm-btn-primary" data-testid="save-channels">{t("inbox.settings.save")}</button>

            <div className="akm-card p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("inbox.settings.send_test")}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <Select label={t("inbox.templates.channel")} value={test.channel} onChange={(v) => setTest({ ...test, channel: v })}
                            options={[["in_app",t("inbox.channel.in_app")],["email",t("inbox.channel.email")],["sms",t("inbox.channel.sms")],["whatsapp",t("inbox.channel.whatsapp")]]} testid="test-channel" />
                    <Input label={t("inbox.settings.to_email")} value={test.to_email} onChange={(v) => setTest({ ...test, to_email: v })} testid="test-email" />
                    <Input label={t("inbox.settings.to_phone")} value={test.to_phone} onChange={(v) => setTest({ ...test, to_phone: v })} testid="test-phone" />
                    <div className="flex items-end">
                        <button onClick={sendTest} className="akm-btn-primary w-full" data-testid="test-send">
                            <Send size={12} /> {t("inbox.settings.test_btn")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Hint({ configured }) {
    const { t } = useTranslation();
    return (
        <div className={`mt-2 text-[10px] font-medium uppercase tracking-wider ${configured ? "text-emerald-700" : "text-amber-700"}`}>
            {configured ? t("inbox.settings.ready") : t("inbox.settings.mock_mode")}
        </div>
    );
}

// Shared ─────────────────────────────────────────────────────
function Input({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid} className="akm-input w-full text-[13px]" />
        </label>
    );
}
function Select({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <select value={value || ""} onChange={(e) => onChange(e.target.value)}
                    data-testid={testid} className="akm-input w-full text-[13px]">
                {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
        </label>
    );
}
function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="inbox-modal">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative w-full max-w-lg rounded-xl border border-border bg-background p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-tight">{title}</div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
